import * as React from "react";
import { MediaPlayer } from "@/components";
import { Button, Card, PaginationSimple, Skeleton, Textarea } from "@/components/ui";
import { useAccountSettings, useComplianceChecker, usePlayerSettings, useSocialCoachSession } from "@/utils";
import { usePostFlowContext } from "../SharedPostFlowContext";
import { CoachSettings, WebSettings } from "@/interfaces";
import { TagIcon, Trash2, WandSparkles } from "lucide-react";
import { DetectedPhraseAlert } from "@/components/DetectedPhraseAlert";

interface Props {
  handleCaptionCoach: () => void;
  handleMentions: () => void;
}

export function EditPostStep({ handleCaptionCoach, handleMentions }: Props) {
  const { playerId } = useSocialCoachSession();
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker();
  const {
    promptId,
    message,
    setMessage,
    media,
    removeMedia,
    mentions,
    blockedWords,
    flaggedWords,
    setBlockedWords,
    setFlaggedWords,
    builderLoading,
  } = usePostFlowContext();

  const [currentMediaPage, setCurrentMediaPage] = React.useState(1);
  const totalMedia = media.length;
  const currentMedia = media[currentMediaPage - 1];

  const { hasAccountSetting } = useAccountSettings();
  const { hasSetting } = usePlayerSettings(playerId);
  const allowMediaEditing = !promptId || hasSetting(CoachSettings.POST_CUSTOM_IMAGES, true);
  const allowCaptionEditing = !promptId || hasSetting(CoachSettings.POST_CUSTOM_MESSAGE, true);
  const hasCaptionCoach = allowCaptionEditing && hasSetting(CoachSettings.CAPTION_COACH, true);
  const hasMentionsFeature = allowCaptionEditing && hasAccountSetting(WebSettings.MENTIONS);

  React.useEffect(() => {
    setBlockedWords(findBlockedWords(message || ""));
    setFlaggedWords(findFlaggedWords(message || ""));
  }, [message, findBlockedWords, findFlaggedWords]);

  const handleRemoveMedia = () => {
    if (currentMedia) {
      removeMedia(currentMedia);
      if (currentMediaPage > 1 && currentMediaPage === totalMedia) {
        setCurrentMediaPage(currentMediaPage - 1);
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card className="gap-0 pt-0 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[300px]">
          {/* Left Side: Media */}
          <div className="flex flex-col bg-slate-50 rounded-lg overflow-hidden m-2">
            <div className="flex-1 flex items-center justify-center overflow-hidden rounded-lg max-h-[350px]">
              {currentMedia ? (
                <div className="reviewMediaSection w-full h-full flex items-center justify-center relative group">
                  <div className="postMediaContainer w-full">
                    <MediaPlayer media={currentMedia} />
                  </div>
                  {allowMediaEditing && (
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity size-8"
                      onClick={handleRemoveMedia}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="py-20 flex flex-col items-center gap-2 text-muted-foreground">
                  <span className="text-sm italic">No media attached</span>
                </div>
              )}
            </div>

            {totalMedia > 1 && (
              <div className="pb-4 px-2">
                <PaginationSimple
                  page={currentMediaPage}
                  totalPages={totalMedia}
                  onPageChange={setCurrentMediaPage}
                  hideLabel={true}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {/* Right Side: Caption & Info */}
          <div className="p-4 flex flex-col bg-slate-50 rounded m-2 gap-2">
            <div className={"flex flex-row gap-4 justify-between items-center"}>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Caption</h4>
              <div className={"flex flex-row gap-2 items-center"}>
                {hasMentionsFeature && (
                  <Button variant="ghost" size="sm" onClick={handleMentions} className={"text-slate-600"}>
                    <TagIcon className="size-4" />
                    {mentions.length > 0 ? `Update Mentions` : "Add Mentions"}
                    {mentions.length > 0 && <span className="text-xs text-muted-foreground">({mentions.length})</span>}
                  </Button>
                )}

                {hasCaptionCoach && (
                  <Button variant="ghost" size="sm" onClick={handleCaptionCoach} className={"text-slate-600"}>
                    <WandSparkles />
                    Generate Caption
                  </Button>
                )}
              </div>
            </div>

            <div className="flex grow flex-col gap-6 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
              {builderLoading ? (
                <div className="flex flex-col gap-2 h-full min-h-[300px]">
                  <Skeleton className="h-4 w-[90%]" />
                  <Skeleton className="h-4 w-[95%]" />
                  <Skeleton className="h-4 w-[85%]" />
                  <Skeleton className="h-4 w-[92%]" />
                  <Skeleton className="h-4 w-[88%]" />
                  <Skeleton className="h-4 w-[96%]" />
                  <Skeleton className="h-4 w-[70%]" />
                </div>
              ) : (
                <div className="flex flex-col gap-3 h-full min-h-[300px]">
                  <DetectedPhraseAlert blockedWords={blockedWords} flaggedWords={flaggedWords} />
                  <Textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Write a caption..."
                    className="resize-none flex-1 overflow-y-auto"
                    disabled={!allowCaptionEditing}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
